import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { nafathVerificationsRepository } from "@/repositories";

export interface NafathAddress {
  city: string | null;
  cityEn: string | null;
  district: string | null;
  districtEn: string | null;
  region: string | null;
  regionEn: string | null;
  streetName: string | null;
  postCode: string | null;
  buildingNumber: string | null;
  additionalNumber: string | null;
  shortAddress: string | null;
  latitude: number | null;
  longitude: number | null;
}

export interface NafathIdentity {
  nationalId: string;
  idType: string;
  dobHijri: string | null;
  dobGregorian: string | null;
  address: NafathAddress | null;
  fullNameAr: string | null;
  fullNameEn: string | null;
  gender: string | null;
  nationalityAr: string | null;
  nationalityEn: string | null;
  verificationDate: string | null;
}

export function parseNafathAddress(responsePayload: unknown): NafathAddress | null {
  if (!responsePayload || typeof responsePayload !== "object") return null;

  const payload = responsePayload as Record<string, unknown>;

  // Real Nafath callbacks store identity data at:
  //   response_payload.callback.decodedPayload.nationalAddress
  // Older / mock payloads put nationalAddress at the top level.
  const callback = payload.callback as Record<string, unknown> | undefined;
  const decodedPayload = callback?.decodedPayload as Record<string, unknown> | undefined;

  const addresses =
    (decodedPayload?.nationalAddress as unknown[] | undefined) ??
    (payload.nationalAddress as unknown[] | undefined);

  if (!Array.isArray(addresses) || addresses.length === 0) return null;

  // Find primary address, or fall back to first
  const addr = (addresses.find(
    (a) => (a as Record<string, unknown>).isPrimaryAddress === "true"
  ) || addresses[0]) as Record<string, string | undefined>;

  let latitude: number | null = null;
  let longitude: number | null = null;
  if (addr.locationCoordinates) {
    // Nafath returns "lng lat" format, e.g. "46.68903017 24.67619617"
    // (46.689 = Saudi longitude, 24.676 = Saudi latitude)
    const parts = addr.locationCoordinates.split(/\s+/);
    if (parts.length === 2) {
      const lng = parseFloat(parts[0]);
      const lat = parseFloat(parts[1]);
      if (!isNaN(lng) && !isNaN(lat)) {
        latitude = lat;
        longitude = lng;
      }
    }
  }

  return {
    city: addr.city || null,
    cityEn: addr.cityL2 || null,
    district: addr.district || null,
    districtEn: addr.districtL2 || null,
    region: addr.regionName || null,
    regionEn: addr.regionNameL2 || null,
    streetName: addr.streetName || null,
    postCode: addr.postCode || null,
    buildingNumber: addr.buildingNumber || null,
    additionalNumber: addr.additionalNumber || null,
    shortAddress: addr.shortAddress || null,
    latitude,
    longitude,
  };
}

export function useNafathIdentity() {
  const { user, profile } = useAuth();

  const isVerified = !!profile?.nafath_verified;
  const userId = user?.id;
  const companyId = profile?.company_id;

  const { data, isLoading } = useQuery({
    queryKey: ["nafath-identity", userId, companyId],
    queryFn: async (): Promise<NafathIdentity | null> => {
      const row = await nafathVerificationsRepository.getSelfIdentityRow(
        userId!,
        companyId,
      );

      if (!row?.national_id) return null;

      const r = row as Record<string, unknown>;

      const address = parseNafathAddress(r.response_payload);

      const fullNameAr = [
        r.arabic_first_name, r.arabic_father_name,
        r.arabic_grand_father_name, r.arabic_family_name,
      ].filter(Boolean).join(" ") || null;

      const fullNameEn = [
        r.english_first_name, r.english_father_name,
        r.english_grand_father_name, r.english_family_name,
      ].filter(Boolean).join(" ") || null;

      return {
        nationalId: row.national_id,
        idType: String(row.id_type),
        dobHijri: row.dob_hijri,
        dobGregorian: row.dob_gregorian,
        address,
        fullNameAr,
        fullNameEn,
        gender: (r.gender as string) || null,
        nationalityAr: (r.nationality_ar as string) || null,
        nationalityEn: (r.nationality_en as string) || null,
        verificationDate: (r.updated_at as string) || null,
      };
    },
    enabled: isVerified && !!userId,
    staleTime: 5 * 60 * 1000,
  });

  return {
    nafathIdentity: data ?? null,
    isLoading: isVerified ? isLoading : false,
  };
}
